import { createRouter, createWebHistory } from 'vue-router'
import { getToken, isAuthValid, parseUrlTokenAndSave } from '@/utils/auth'
import { ElMessage } from 'element-plus'
import { useAuthStore } from '@/stores/auth'
import { useMenuPermission } from '@/composables/useMenuPermission'
import FeedbackManagement from '@/views/feedback/FeedbackManagement.vue'
import { createRouteComponent, setupRouterErrorHandling } from '@/utils/routerUtils'
import analytics from '@/utils/analytics' // 导入埋点SDK
import messages from '@/plugins/i18n/messages'
import { isEmailChangeCurrentConfirmationRoute, rememberPendingEmailChangeRedirect } from '@/utils/emailChangeRedirect'

// 路由守卫中的简易翻译函数（非组件上下文无法使用 useI18n）
const routerT = (key) => {
  const locale = localStorage.getItem('locale') || 'zh-CN'
  const keys = key.split('.')
  let result = messages[locale]
  for (const k of keys) {
    if (result && typeof result === 'object') {
      result = result[k]
    } else {
      return key
    }
  }
  return typeof result === 'string' ? result : key
}

// 路由配置
const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue').catch(error => {
      console.error('Failed to load Login component:', error);
      return import('@/views/error/ComponentLoadError.vue');
    }),
    meta: { titleKey: 'router.login' }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/Register.vue').catch(error => {
      console.error('Failed to load Register component:', error);
      return import('@/views/error/ComponentLoadError.vue');
    }),
    meta: { titleKey: 'router.register' }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPassword.vue').catch(error => {
      console.error('Failed to load ForgotPassword component:', error);
      return import('@/views/error/ComponentLoadError.vue');
    }),
    meta: { titleKey: 'router.forgotPassword' }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/ResetPassword.vue').catch(error => {
      console.error('Failed to load ResetPassword component:', error);
      return import('@/views/error/ComponentLoadError.vue');
    }),
    meta: { titleKey: 'router.resetPassword' }
  },
  {
    path: '/activate',
    name: 'Activate',
    component: () => import('@/views/Activate.vue').catch(error => {
      console.error('Failed to load Activate component:', error);
      return import('@/views/error/ComponentLoadError.vue');
    }),
    meta: { titleKey: 'router.activate' }
  },
  // 注册申请功能已改为使用外部飞书表单，以下路由已注释
  // {
  //   path: '/registration/apply',
  //   name: 'RegistrationApply',
  //   component: () => import('@/views/registration/apply.vue').catch(error => {
  //     console.error('Failed to load RegistrationApply component:', error);
  //     return import('@/views/error/ComponentLoadError.vue');
  //   }),
  //   meta: { 
  //     title: '注册申请',
  //     public: true,  // 公开页面标记
  //     requiresAuth: false  // 明确不需要认证
  //   }
  // },
  {
    path: '/',
    component: () => import('@/layout/index.vue'),
    redirect: () => {
      try {
        const userInfoStr = localStorage.getItem('user_info') || localStorage.getItem('user')
        if (userInfoStr) {
          const userInfo = JSON.parse(userInfoStr)
          const role = String(userInfo.role || '').toUpperCase()
          const isAdmin = userInfo.username === 'admin' ||
            role === 'ADMIN' ||
            role === 'SUPER_ADMIN'
          if (isAdmin) return '/targets'
          // 运营/产品进入后台工作台（有 backend 菜单）
          if (role === 'OPERATOR' || role === 'PRODUCT') return '/workspace'
        }
      } catch (e) {
        console.warn('解析用户信息失败，使用默认首页:', e)
      }
      return '/target-analysis-v2'
    },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/index.vue'),
        meta: { titleKey: 'router.dashboard', requiresAuth: true }
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('../views/users/UserList.vue'),
        meta: { title: '用户管理', requiresAuth: true, adminOnly: true, requiresPermission: 'users:view' }
      },
      {
        path: 'tenants',
        name: 'tenants',
        component: () => import('@/views/tenants/TenantList.vue'),
        meta: { title: '租户管理', requiresAuth: true, adminOnly: true, requiresPermission: 'tenants:view' }
      },
      {
        path: 'invitation-codes',
        name: 'invitation-codes',
        component: () => import('@/views/invitation-codes/InvitationCodeList.vue'),
        meta: { title: '邀请码管理', requiresAuth: true, adminOnly: true }
      },
      {
        path: 'invitation-codes/:id',
        name: 'invitation-code-detail',
        component: () => import('@/views/invitation-codes/InvitationCodeDetail.vue'),
        meta: { title: '邀请码详情', requiresAuth: true, adminOnly: true }
      },


      {
        path: 'reports',
        name: 'reports',
        component: () => import('../views/reports/ReportView.vue')
      },
      // 调研分析路由（管理员）
      {
        path: 'targets',
        name: 'targets',
        component: () => import('@/views/targets/TargetList.vue'),
        meta: { title: '任务管理', requiresAuth: true, adminOnly: true, requiresPermission: 'targets:view' }
      },
      {
        path: 'targets/:id',
        name: 'TargetDetail',
        component: () => import('@/views/targets/TargetDetail.vue'),
        meta: { titleKey: 'router.targetDetail', requiresAuth: true, adminOnly: true }
      },
      // 普通用户的靶点报告详情页面
      {
        path: 'target-reports/:id',
        name: 'TargetReportDetail',
        component: () => import('@/views/targets/TargetReportDetail.vue'),
        meta: { titleKey: 'router.targetReportDetail', requiresAuth: true }
      },
      // 幻觉检测独立页面（可单独打开幻觉检测报告）
      {
        path: 'target-reports/:id/hallucination',
        name: 'HallucinationDetection',
        component: () => import('@/views/targets/HallucinationDetectionPage.vue'),
        // fullPage: 使用用户工作区侧栏，避免普通用户看到 admin 后台菜单
        meta: { titleKey: 'router.hallucination', requiresAuth: true, fullPage: true }
      },
      {
        path: 'target-reports/:id/interactive-edit',
        name: 'ReportInteractiveEdit',
        component: () => import('@/views/targets/ReportInteractiveEditPage.vue'),
        // fullPage: 使用用户工作区侧栏，避免普通用户看到 admin 后台菜单
        meta: { titleKey: 'router.interactiveEdit', requiresAuth: true, fullPage: true }
      },
      {
        path: 'target-reports/:id/intelligence-subscription',
        name: 'TargetReportIntelligenceSubscription',
        component: () => import('@/views/targets/IntelligenceSubscription.vue'),
        // fullPage: 使用用户工作区侧栏，避免普通用户看到 admin 后台菜单
        meta: { titleKey: 'router.intelligenceSubscription', requiresAuth: true, fullPage: true }
      },
      {
        path: 'target-reports/:id/intelligence-feed',
        name: 'TargetReportIntelligenceFeed',
        component: () => import('@/views/targets/IntelligenceFeed.vue'),
        meta: { titleKey: 'router.intelligenceFeed', requiresAuth: true, fullPage: true }
      },
      {
        path: 'targets/:id/chat',
        name: 'TargetChat',
        component: () => import('@/views/targets/TargetChat.vue'),
        meta: { titleKey: 'router.targetChat', requiresAuth: true, adminOnly: true }
      },
      {
        path: 'targets/:id/intelligence-subscription',
        name: 'TargetIntelligenceSubscription',
        component: () => import('@/views/targets/IntelligenceSubscription.vue'),
        meta: { titleKey: 'router.intelligenceSubscription', requiresAuth: true, adminOnly: true }
      },
      {
        path: 'targets/:id/intelligence-feed',
        name: 'TargetIntelligenceFeed',
        component: () => import('@/views/targets/IntelligenceFeed.vue'),
        meta: { titleKey: 'router.intelligenceFeed', requiresAuth: true, adminOnly: true }
      },
      // 普通用户调研分析：保留 TargetAnalysis 路由名，默认重定向到 V2（兼容旧链接与 query）
      {
        path: 'target-analysis',
        name: 'TargetAnalysis',
        redirect: (to) => ({
          path: '/target-analysis-v2',
          query: to.query,
        }),
      },
      // {
      //   path: 'target-analysis-v1',
      //   name: 'TargetAnalysisV1',
      //   component: () => import('@/views/targets/TargetAnalysisChat.vue'),
      //   meta: { title: '任务分析V1', requiresAuth: true, adminOnly: true }
      // },
      // 调研分析 V2（重构联调用）
      // fullPage: 跳过 Layout 的 admin 顶栏/侧栏，由页面自带 AppSidebar 排版
      {
        path: 'target-analysis-v2',
        name: 'TargetAnalysisV2',
        component: () => import('@/views/targets/TargetAnalysisV2.vue'),
        meta: { titleKey: 'router.targetAnalysisV2', requiresAuth: true, fullPage: true }
      },
      {
        path: 'targets/v2/:id',
        name: 'TargetAnalysisV2Detail',
        component: () => import('@/views/targets/TargetAnalysisV2.vue'),
        meta: { titleKey: 'router.targetAnalysisV2', requiresAuth: true, fullPage: true }
      },
      {
        path: 'quick-tools',
        name: 'QuickTools',
        component: () => import('@/views/quick-tools/QuickToolsView.vue'),
        meta: { titleKey: 'router.quickTools', requiresAuth: true, fullPage: true }
      },
      {
        path: 'quick-tools/create-template',
        name: 'QuickToolsCreateTemplate',
        component: () => import('@/views/quick-tools/QuickToolsCreateTemplateView.vue'),
        meta: { titleKey: 'router.quickToolsCreateTemplate', requiresAuth: true, fullPage: true }
      },
      {
        path: 'quick-tools/edit-expert-template',
        name: 'QuickToolsEditExpertTemplate',
        component: () => import('@/views/quick-tools/QuickToolsExpertTemplateEditView.vue'),
        meta: { titleKey: 'router.quickToolsEditExpert', requiresAuth: true, fullPage: true }
      },
      {
        path: 'quick-tools/quick-template',
        name: 'QuickToolsQuickTemplateEdit',
        component: () => import('@/views/quick-tools/QuickToolsQuickTemplateEditView.vue'),
        meta: { titleKey: 'router.quickToolsEditQuick', requiresAuth: true, fullPage: true }
      },
      {
        path: 'quick-tools/outline-templates',
        name: 'QuickToolsOutlineTemplates',
        redirect: { name: 'TargetAnalysisV2', query: { openQuickTools: 'outline-template-manager' } },
        meta: { titleKey: 'router.outlineTemplates', requiresAuth: true }
      },
      // 知识地图路由
      // fullPage: 跳过 admin navbar/sidebar，由页面自带 AppSidebar 排版
      {
        path: 'knowledge-map',
        name: 'KnowledgeMap',
        component: () => import('@/views/knowledge-map/KnowledgeMapView.vue'),
        meta: { titleKey: 'router.knowledgeMap', requiresAuth: true, fullPage: true }
      },
      {
        path: 'knowledge-map/:topicId/subscription',
        name: 'KnowledgeMapSubscriptionConfig',
        component: () => import('@/views/knowledge-map/KnowledgeSubscriptionConfig.vue'),
        meta: { titleKey: 'router.knowledgeMapSubscription', requiresAuth: true, fullPage: true }
      },
      {
        path: 'knowledge-map/:topicId/subscription-feed',
        name: 'KnowledgeMapSubscriptionFeed',
        component: () => import('@/views/knowledge-map/KnowledgeSubscriptionFeed.vue'),
        meta: { titleKey: 'router.knowledgeMapSubscriptionFeed', requiresAuth: true, fullPage: true }
      },
      {
        path: 'knowledge-map-v2',
        name: 'KnowledgeMapV2',
        component: () => import('@/views/knowledge-map/KnowledgeMapV2View.vue'),
        meta: { titleKey: 'router.knowledgeMap', requiresAuth: true, fullPage: true }
      },
      // 知识库路由
      // fullPage: 跳过 admin navbar/sidebar，由页面自带 AppSidebar 排版
      {
        path: 'knowledge-base',
        name: 'KnowledgeBase',
        component: () => import('@/views/knowledge-base/KnowledgeBaseView.vue'),
        meta: { titleKey: 'router.knowledgeBase', requiresAuth: true, fullPage: true, requiresPermission: 'knowledge_base:access' }
      },
      // 用户手册路由（fullPage + AppSidebar）
      {
        path: 'user-manual',
        name: 'UserManual',
        component: () => import('@/views/user-manual/UserManual.vue'),
        meta: { titleKey: 'router.userManual', requiresAuth: true, fullPage: true }
      },
      // 个人信息页面路由（fullPage + AppSidebar）
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/profile/index.vue'),
        meta: { titleKey: 'router.profile', requiresAuth: true, fullPage: true }
      },
      // 用户偏好统计页面路由（fullPage + AppSidebar）
      {
        path: 'user-preference-statistics',
        name: 'UserPreferenceStatistics',
        component: () => import('@/views/user/UserPreferenceStatistics.vue'),
        meta: { titleKey: 'router.preferenceStats', requiresAuth: true, fullPage: true }
      },
      // 用户偏好画像中心（fullPage + AppSidebar）
      {
        path: 'preference-center',
        name: 'UserPreferenceCenter',
        component: () => import('@/views/user/UserPreferenceCenter.vue'),
        alias: ['/preference-center'],
        meta: { titleKey: 'router.preferenceCenter', requiresAuth: true, fullPage: true }
      },
      // 用户反馈页：重定向到个人中心 -> 用户反馈 Tab，与"通知中心"保持同样的交互
      {
        path: 'feedback',
        name: 'UserFeedback',
        redirect: { path: '/account', query: { main: 'feedback' } },
        meta: { titleKey: 'router.feedback', requiresAuth: true, fullPage: true }
      },
      // 个人账户页面路由（fullPage + AppSidebar）
      {
        path: 'account',
        name: 'Account',
        component: () => import('@/views/account/index.vue').catch(error => {
          console.error('Failed to load Account component:', error);
          return import('@/views/error/ComponentLoadError.vue');
        }),
        meta: { titleKey: 'router.account', requiresAuth: true, fullPage: true }
      },
      // 邀请码管理页面路由
      {
        path: 'invite-code-management',
        name: 'InviteCodeManagement',
        component: () => import('@/views/invite-reward/InviteCodeManagement.vue'),
        meta: { titleKey: 'router.inviteCodeManagement', requiresAuth: true }
      },
      // 充值中心页面路由（fullPage + AppSidebar）
      {
        path: 'payment-center',
        name: 'PaymentCenter',
        component: () => import('@/views/account/PaymentPage.vue').catch(error => {
          console.error('Failed to load PaymentCenter component:', error);
          return import('@/views/error/ComponentLoadError.vue');
        }),
        meta: { titleKey: 'router.paymentCenter', requiresAuth: true, fullPage: true }
      },
      // 用户tokens管理路由
      {
        path: 'user-tokens',
        name: 'UserTokens',
        component: () => import('@/views/user-tokens/index.vue'),
        meta: { title: '用户额度管理', requiresAuth: true, adminOnly: true }
      },
      // AI模型管理路由
      {
        path: 'ai-models',
        name: 'AIModels',
        component: () => import('@/views/ai-models/index.vue'),
        meta: { title: 'AI模型管理', requiresAuth: true, adminOnly: true }
      },
      // 大纲模板管理路由
      {
        path: 'outline-templates',
        name: 'OutlineTemplateManagement',
        component: () => import('@/views/admin/OutlineTemplateManagement.vue'),
        meta: { title: '模板管理', requiresAuth: true, adminOnly: true }
      },
      // 知识库管理路由
      {
        path: 'kb-management',
        redirect: '/kb-management/rag-list',
        meta: { title: '知识库管理', requiresAuth: true, adminOnly: true }
      },
      {
        path: 'kb-management/rag-list',
        name: 'RAGKnowledgeBaseList',
        component: () => import('@/views/admin/knowledge-base/RAGKnowledgeBaseList.vue'),
        meta: { title: 'RAG知识库', requiresAuth: true, adminOnly: true }
      },
      {
        path: 'kb-management/user-files',
        name: 'UserFileManagement',
        component: () => import('@/views/admin/knowledge-base/UserFileManagement.vue'),
        meta: { title: '用户文件管理', requiresAuth: true, adminOnly: true }
      },
      {
        path: 'kb-management/entity-memory',
        name: 'EntityMemoryManagement',
        component: () => import('@/views/admin/knowledge-base/EntityMemoryConsole.vue'),
        meta: { title: '知识图谱记忆', requiresAuth: true, adminOnly: true }
      },
      // Agent Studio 已移动到智能体管理菜单下（见下方 agent-management/studio）
      // 保留此路由用于兼容旧链接，重定向到新位置
      {
        path: 'agent-studio',
        redirect: '/agent-management/studio',
        meta: { title: 'Agent Studio', requiresAuth: true, adminOnly: true }
      },
      // 汇率管理路由
      {
        path: 'exchange-rate',
        name: 'ExchangeRate',
        component: () => import('@/views/admin/ExchangeRateManagement.vue'),
        meta: { title: '汇率管理', requiresAuth: true, adminOnly: true }
      },
      // 价格历史管理路由
      {
        path: 'price-history',
        name: 'PriceHistory',
        component: () => import('@/views/admin/PriceHistoryManagement.vue'),
        meta: { title: '价格历史', requiresAuth: true, adminOnly: true }
      },
      // token使用记录路由
      {
        path: 'token-usage',
        name: 'TokenUsage',
        component: () => import('@/views/token-usage/index.vue'),
        meta: { title: '额度使用记录', requiresAuth: true, adminOnly: true, requiresPermission: 'token_usage:view' }
      },
      // token统计分析路由
      {
        path: 'token-statistics',
        name: 'TokenStatistics',
        component: () => import('@/views/token-statistics/TokenStatisticsView.vue'),
        meta: { title: 'Token消耗统计', requiresAuth: true, adminOnly: true }
      },
      {
        path: 'operation-logs',
        name: 'OperationLogs',
        component: () => import('@/views/operation-logs/OperationLogsView.vue'),
        meta: { title: '操作日志', requiresAuth: true, adminOnly: true }
      },
      // 用户反馈管理路由
      {
        path: 'feedback-management',
        name: 'FeedbackManagement',
        component: FeedbackManagement,
        meta: { title: '用户反馈管理', requiresAuth: true, adminOnly: true, requiresPermission: 'feedback:manage' }
      },
      // 注册申请管理路由（已注释，使用外部飞书表单）
      // {
      //   path: 'registration-management',
      //   name: 'RegistrationManagement',
      //   component: () => import('@/views/registration/manage.vue'),
      //   meta: { title: '注册申请管理', requiresAuth: true, adminOnly: true }
      // },
      // 邀请奖励配置管理路由（管理员）
      {
        path: 'invite-reward-config',
        name: 'InviteRewardConfig',
        component: () => import('@/views/admin/InviteRewardConfig.vue'),
        meta: { title: '邀请奖励配置', requiresAuth: true, adminOnly: true }
      },
      // 邀请奖励审核管理路由（管理员）
      {
        path: 'invite-reward-review',
        name: 'InviteRewardReview',
        component: () => import('@/views/admin/InviteRewardReview.vue'),
        meta: { title: '邀请奖励审核', requiresAuth: true, adminOnly: true }
      },
      // 用户邀请统计页面路由（管理员）
      {
        path: 'user-invite-statistics',
        name: 'UserInviteStatistics',
        component: () => import('@/views/invite-reward/UserInviteStatistics.vue'),
        meta: { title: '用户邀请统计', requiresAuth: true, adminOnly: true }
      },
      // 邀请营销页面路由（管理员）
      {
        path: 'invite-marketing',
        name: 'InviteMarketing',
        component: () => import('@/views/invite-reward/InviteMarketing.vue'),
        meta: { title: '邀请营销', requiresAuth: true, adminOnly: true }
      },
      // ========== 一级父菜单路由(重定向到第一个子菜单) ==========
      // 工作台父路由
      {
        path: 'workspace',
        redirect: '/dashboard',
        meta: { title: '工作台', requiresAuth: true, adminOnly: true, requiresPermission: 'workspace:view' }
      },
      // 用户与租户父路由
      {
        path: 'user-tenant',
        redirect: '/users',
        meta: { title: '用户与租户', requiresAuth: true, adminOnly: true, requiresPermission: 'user_tenant:view' }
      },
      // 营销管理父路由
      {
        path: 'marketing',
        redirect: '/invitation-codes',
        meta: { title: '营销管理', requiresAuth: true, adminOnly: true }
      },
      // 运营中心父路由
      {
        path: 'operation',
        redirect: '/feedback-management',
        meta: { title: '运营中心', requiresAuth: true, adminOnly: true, requiresPermission: 'operation:view' }
      },
      // 系统管理父路由
      {
        path: 'system',
        redirect: '/ai-models',
        meta: { title: '系统管理', requiresAuth: true, adminOnly: true }
      },
      // 任务监控路由（系统配置子菜单）
      {
        path: 'task-monitor',
        name: 'TaskMonitor',
        component: () => import('@/views/admin/TaskMonitor.vue'),
        meta: { title: '任务监控', requiresAuth: true, adminOnly: true }
      },
      // Agent服务配置路由（系统管理子菜单）
      {
        path: 'system-config-management',
        name: 'SystemConfigManagement',
        component: () => import('@/views/admin/SystemConfigManagement.vue'),
        meta: { title: 'Agent服务配置', requiresAuth: true, adminOnly: true }
      },
      // 环境配置管理路由（系统管理子菜单）
      {
        path: 'env-config-management',
        name: 'EnvConfigManagement',
        component: () => import('@/views/admin/EnvConfigManagement.vue'),
        meta: { title: '环境配置管理', requiresAuth: true, adminOnly: true }
      },
      // 系统巡检路由（系统管理子菜单）
      {
        path: 'system-inspection',
        name: 'SystemInspection',
        component: () => import('@/views/admin/SystemInspection.vue'),
        meta: { title: '系统巡检', requiresAuth: true, adminOnly: true }
      },
      // 环境配置管理路由（新路径，绕过浏览器缓存）
      {
        path: 'env-management',
        name: 'EnvManagement',
        component: () => import('@/views/admin/EnvConfigManagementNew.vue'),
        meta: { title: '环境配置管理', requiresAuth: true, adminOnly: true }
      },
      // 测试路由
      {
        path: 'env-test',
        name: 'EnvTest',
        component: () => import('@/views/admin/EnvConfigTest.vue'),
        meta: { title: '环境配置测试', requiresAuth: true, adminOnly: true }
      },
      // 工具测试路由
      {
        path: 'tool-test',
        name: 'ToolTest',
        component: () => import('@/views/admin/ToolTestPage.vue'),
        meta: { title: '工具测试', requiresAuth: true, adminOnly: true }
      },
      // 权限管理父路由
      {
        path: 'permission',
        redirect: '/role-management',
        meta: { title: '权限管理', requiresAuth: true, adminOnly: true }
      },
      
      // ========== 财务中心路由 ==========
      // 财务中心父路由（重定向到充值管理）
      {
        path: 'finance',
        redirect: '/admin-recharge-management',
        meta: { title: '财务中心', requiresAuth: true, adminOnly: true }
      },
      // 充值管理页面路由
      {
        path: 'admin-recharge-management',
        name: 'AdminRechargeManagement',
        component: () => import('@/views/admin/RechargeManagement.vue'),
        meta: { title: '充值管理', requiresAuth: true, adminOnly: true }
      },
      // 管理员充值页面路由
      {
        path: 'admin-recharge',
        name: 'AdminRecharge',
        component: () => import('@/views/admin/AdminRecharge.vue'),
        meta: { title: '后台充值', requiresAuth: true, adminOnly: true }
      },
      // X-Coin充值套餐管理路由（管理员）
      {
        path: 'xcoin-package-management',
        name: 'XCoinPackageManagement',
        component: () => import('@/views/admin/XCoinPackageManagement.vue'),
        meta: { title: 'X-Coin套餐管理', requiresAuth: true, adminOnly: true }
      },
      // 会员中心管理路由（管理员）
      {
        path: 'admin-membership',
        name: 'AdminMembership',
        component: () => import('@/views/admin/AdminMembership.vue'),
        meta: { title: '会员中心管理', requiresAuth: true, adminOnly: true }
      },
      // 奖励余额明细管理路由（管理员）
      {
        path: 'reward-balance-management',
        name: 'RewardBalanceManagement',
        component: () => import('@/views/admin/RewardBalanceManagement.vue'),
        meta: { title: '奖励余额明细', requiresAuth: true, adminOnly: true }
      },
      // X-Coin交易记录管理（管理员，财务中心子菜单）
      {
        path: 'xcoin-transaction-management',
        name: 'XCoinTransactionManagement',
        component: () => import('@/views/admin/XCoinTransactionManagement.vue'),
        meta: { title: 'X-Coin交易记录管理', requiresAuth: true, adminOnly: true }
      },
      // XC发放记录监控（管理员，财务中心子菜单）
      {
        path: 'xcoin-grant-records',
        name: 'XCoinGrantRecords',
        component: () => import('@/views/xcoin-monitoring/XCoinGrantRecordsView.vue'),
        meta: { title: 'XC发放记录', requiresAuth: true, adminOnly: true }
      },
      // XC发放失败日志（管理员，财务中心子菜单）
      {
        path: 'xcoin-grant-failure-logs',
        name: 'XCoinGrantFailureLogs',
        component: () => import('@/views/xcoin-monitoring/XCoinGrantFailureLogsView.vue'),
        meta: { title: 'XC发放失败日志', requiresAuth: true, adminOnly: true }
      },
      // Ping++支付配置路由（管理员）
      {
        path: 'payment-config',
        name: 'PaymentConfig',
        component: () => import('@/views/admin/PaymentConfig.vue'),
        meta: { title: 'Ping++支付配置', requiresAuth: true, adminOnly: true }
      },
      // 通知管理页面，仅管理员可见
      {
        path: 'notification-management',
        name: 'NotificationManagement',
        component: () => import('@/views/notifications/NotificationManagement.vue'),
        meta: { 
          title: '通知管理',
          requiresAuth: true,
          adminOnly: true,
          requiresPermission: 'notification:manage'
        }
      },
      // Flower 监控路由
      {
        path: 'flower-monitoring',
        name: 'FlowerMonitoring',
        component: () => import('@/views/flower/FlowerMonitoring.vue'),
        meta: { title: 'Celery 任务监控', requiresAuth: true, adminOnly: true }
      },
      // 报告评价管理
      {
        path: 'report-rating-management',
        name: 'ReportRatingManagement',
        component: () => import('@/views/admin/ReportRatingManagement.vue'),
        meta: { title: '报告评价管理', requiresAuth: true, adminOnly: true, requiresPermission: 'report:rating:manage' }
      },
      // 模型使用分析（运营中心子菜单，管理员和超级管理员）
      {
        path: 'operation/model-analytics',
        name: 'ModelAnalyticsDashboard',
        component: () => import('@/views/admin/ModelAnalyticsDashboard.vue'),
        alias: ['/admin/model-analytics', '/operation/model-analytics'],
        meta: { title: '模型使用分析', requiresAuth: true, adminOnly: true }
      },
      // Worker 详情路由
      {
        path: 'flower/worker/:workerName',
        name: 'WorkerDetail',
        component: () => import('../views/flower/WorkerDetail.vue'),
        meta: { title: 'Worker 详情', requiresAuth: true, adminOnly: true }
      },
      // IP安全管理路由
      {
        path: 'ip-security',
        name: 'IPSecurityManagement',
        component: () => import('@/views/ip-security/IPSecurityManagement.vue'),
        meta: { title: 'IP安全管理', requiresAuth: true, adminOnly: true }
      },
      // 邀请码审计日志路由
      {
        path: 'audit-logs',
        name: 'InvitationCodeAuditLogs',
        component: () => import('@/views/audit-logs/InvitationCodeAuditLogs.vue'),
        meta: { title: '邀请码审计日志', requiresAuth: true, adminOnly: true }
      },
      // 行为奖励配置管理路由（管理员）
      {
        path: 'behavior-reward-config',
        name: 'BehaviorRewardConfig',
        component: () => import('@/views/admin/BehaviorRewardConfig.vue'),
        meta: { title: '行为奖励配置', requiresAuth: true, adminOnly: true }
      },
      // 行为奖励任务监控路由（管理员）
      {
        path: 'behavior-reward-tasks',
        name: 'BehaviorRewardTasks',
        component: () => import('@/views/admin/BehaviorRewardTasks.vue'),
        meta: { title: '行为奖励任务', requiresAuth: true, adminOnly: true }
      },
      // 行为奖励统计路由（管理员）
      {
        path: 'behavior-reward-statistics',
        name: 'BehaviorRewardStatistics',
        component: () => import('@/views/admin/BehaviorRewardStatistics.vue'),
        meta: { title: '行为奖励统计', requiresAuth: true, adminOnly: true }
      },
      // 用户奖励查询路由（管理员）
      {
        path: 'user-reward-query',
        name: 'UserRewardQuery',
        component: () => import('@/views/admin/UserRewardQuery.vue'),
        meta: { title: '用户奖励查询', requiresAuth: true, adminOnly: true }
      },
      
      // ========== 智能体管理 (扁平化子路由，兼容/admin前缀) ==========
      // 智能体管理父路由重定向
      {
        path: 'agent-management',
        redirect: '/agent-management/agents',
        meta: { title: '智能体管理', requiresAuth: true, adminOnly: true }
      },
      // Agent Studio - 智能体流程图编辑器（从一级菜单移至此处）
      {
        path: 'agent-management/studio',
        name: 'AgentStudio',
        component: () => import('@/views/AgentStudio/index.vue').catch(error => {
          console.error('Failed to load AgentStudio component:', error);
          return import('@/views/error/ComponentLoadError.vue');
        }),
        meta: { title: 'Agent Studio', requiresAuth: true, adminOnly: true }
      },
      // 智能体列表
      {
        path: 'agent-management/agents',
        name: 'AgentList',
        component: () => import('@/views/admin/AgentManagementPage.vue'),
        meta: { title: '智能体列表', requiresAuth: true, adminOnly: true }
      },
      // 对话测试器
      {
        path: 'agent-management/conversation-tester',
        name: 'ConversationTester',
        component: () => import('@/views/admin/ConversationTesterPage.vue'),
        meta: { title: '对话测试', requiresAuth: true, adminOnly: true }
      },
      // Checkpoint管理
      {
        path: 'agent-management/checkpoint-manager',
        name: 'CheckpointManager',
        component: () => import('@/views/admin/CheckpointManagerPage.vue'),
        meta: { title: 'Checkpoint管理', requiresAuth: true, adminOnly: true }
      },
      // Phase 3: StateGraph调试器
      {
        path: 'agent-management/state-graph-debugger',
        name: 'StateGraphDebugger',
        component: () => import('@/views/admin/StateGraphDebuggerPage.vue'),
        meta: { title: 'StateGraph调试器', requiresAuth: true, adminOnly: true }
      },
      // Phase 3: 性能分析
      {
        path: 'agent-management/performance-dashboard',
        name: 'PerformanceDashboard',
        component: () => import('@/views/admin/PerformanceDashboardPage.vue'),
        meta: { title: '性能分析', requiresAuth: true, adminOnly: true }
      },
      // Phase 3.1: 配置管理
      {
        path: 'agent-management/config-manager',
        name: 'ConfigManager',
        component: () => import('@/views/admin/ConfigManagerPage.vue'),
        meta: { title: '配置管理', requiresAuth: true, adminOnly: true }
      },
      // PROMPT管理
      {
        path: 'agent-management/prompt-management',
        name: 'PromptManagement',
        component: () => import('@/views/admin/PromptManagementPage.vue'),
        meta: { title: 'PROMPT管理', requiresAuth: true, adminOnly: true }
      },
      // 工具测试
      {
        path: 'agent-management/tool-test',
        name: 'AgentToolTest',
        component: () => import('@/views/admin/ToolTestPage.vue'),
        meta: { title: '工具测试', requiresAuth: true, adminOnly: true }
      },
      // 工具调用统计
      {
        path: 'agent-management/tool-call-statistics',
        name: 'ToolCallStatistics',
        component: () => import('@/views/admin/ToolCallStatisticsView.vue'),
        meta: { title: '工具调用统计', requiresAuth: true, adminOnly: true }
      },
      
      // ========== 兼容旧的/admin前缀路径（别名路由） ==========
      {
        path: 'admin/agent-management',
        redirect: '/agent-management/agents',
        meta: { title: '智能体管理', requiresAuth: true, adminOnly: true }
      },
      {
        path: 'admin/agent-management/agents',
        redirect: '/agent-management/agents',
        meta: { title: '智能体列表', requiresAuth: true, adminOnly: true }
      },
      {
        path: 'admin/agent-management/prompt-management',
        redirect: '/agent-management/prompt-management',
        meta: { title: 'PROMPT管理', requiresAuth: true, adminOnly: true }
      },
      {
        path: 'admin/agent-management/tool-test',
        redirect: '/agent-management/tool-test',
        meta: { title: '工具测试', requiresAuth: true, adminOnly: true }
      },
      
      // 角色管理路由（管理员）
      {
        path: 'role-management',
        name: 'RoleManagement',
        component: () => import('@/views/admin/RoleManagement.vue'),
        meta: { title: '角色管理', requiresAuth: true, adminOnly: true }
      },
      // 菜单管理路由（管理员）
      {
        path: 'menu-management',
        name: 'MenuManagement',
        component: () => import('@/views/admin/MenuManagement.vue'),
        meta: { title: '菜单管理', requiresAuth: true, adminOnly: true }
      },
      // 字典管理路由（管理员）
      {
        path: 'dict-management',
        name: 'DictManagement',
        component: () => import('@/views/admin/DictManagement.vue'),
        meta: { title: '字典管理', requiresAuth: true, adminOnly: true }
      },
      // 短信服务管理路由（管理员）
      {
        path: 'sms-management',
        name: 'SmsManagement',
        component: () => import('@/views/admin/SmsManagement.vue'),
        meta: { title: '短信服务管理', requiresAuth: true, adminOnly: true }
      },
      // 敏感词管理路由（管理员）
      {
        path: 'sensitive-words',
        name: 'SensitiveWords',
        component: () => import('@/views/admin/SensitiveWords.vue'),
        meta: { title: '敏感词管理', requiresAuth: true, adminOnly: true }
      },
      // 算法安全日志管理路由（管理员）
      {
        path: 'algorithm-security-logs',
        name: 'AlgorithmSecurityLogs',
        component: () => import('@/views/admin/AlgorithmSecurityLogs.vue'),
        meta: { title: '审核管理', requiresAuth: true, adminOnly: true }
      },
      // 幻觉检测记录管理（管理员）
      {
        path: 'hallucination-detection-management',
        name: 'HallucinationDetectionManagement',
        component: () => import('@/views/admin/HallucinationDetectionManagement.vue'),
        meta: { title: '幻觉检测记录', requiresAuth: true, adminOnly: true }
      },
      // 生产日志监控（管理员）
      {
        path: 'log-monitoring',
        name: 'LogMonitoring',
        component: () => import('@/views/admin/LogMonitoring.vue'),
        meta: { title: '生产日志监控', requiresAuth: true, adminOnly: true }
      },
      // 算法安全日志对话详情（管理员）
      {
        path: 'algorithm-security-logs/conversation/:id',
        name: 'AlgorithmSecurityLogConversationDetail',
        component: () => import('@/views/admin/AlgorithmSecurityLogConversationDetail.vue'),
        meta: { title: '对话详情', requiresAuth: true, adminOnly: true }
      },
      // 任务对话审核详情（管理员）
      {
        path: 'algorithm-security-logs/target/:id',
        name: 'TaskConversationDetail',
        component: () => import('@/views/admin/TaskConversationDetail.vue'),
        meta: { title: '任务详情', requiresAuth: true, adminOnly: true }
      },
      // 审核管理路由（管理员）
      {
        path: 'content-review',
        name: 'ContentReviewManagement',
        component: () => import('@/views/admin/ContentReviewManagement.vue'),
        meta: { title: '审核管理', requiresAuth: true, adminOnly: true }
      },
      // 权限分配路由（管理员）
      {
        path: 'permission-management',
        name: 'PermissionManagement',
        component: () => import('@/views/admin/PermissionManagement.vue'),
        meta: { title: '权限分配', requiresAuth: true, adminOnly: true }
      },
      // 用户分析仪表盘路由（管理员）
      {
        path: 'department-analytics',
        name: 'DepartmentAnalytics',
        component: () => import('@/views/analytics/DepartmentAnalytics.vue'),
        meta: { title: '用户分析仪表盘', requiresAuth: true, adminOnly: true }
      },
      // 埋点管理路由（管理员）
      {
        path: 'analytics-admin/dashboard',
        name: 'AnalyticsDashboard',
        component: () => import('@/views/analytics/AnalyticsDashboardEnhanced.vue'),
        meta: { title: '埋点数据概览', requiresAuth: true, adminOnly: true }
      },
      {
        path: 'analytics-admin/events',
        name: 'AnalyticsEventList',
        component: () => import('@/views/analytics/EventList.vue'),
        meta: { title: '事件列表', requiresAuth: true, adminOnly: true }
      },
      {
        path: 'analytics-admin/pages',
        name: 'AnalyticsPageAnalysis',
        component: () => import('@/views/analytics/PageAnalysis.vue'),
        meta: { title: '页面分析', requiresAuth: true, adminOnly: true }
      },
      {
        path: 'analytics-admin/buttons',
        name: 'AnalyticsButtonAnalysis',
        component: () => import('@/views/analytics/ButtonAnalysis.vue'),
        meta: { title: '按钮分析', requiresAuth: true, adminOnly: true }
      },
      {
        path: 'analytics-admin/heatmap',
        name: 'AnalyticsHeatmap',
        component: () => import('@/views/analytics/HeatmapView.vue'),
        meta: { title: '热力图', requiresAuth: true, adminOnly: true }
      },
      {
        path: 'analytics-admin/feature-usage',
        name: 'AnalyticsFeatureUsage',
        component: () => import('@/views/analytics/FeatureUsageView.vue'),
        meta: { title: '功能使用频率', requiresAuth: true, adminOnly: true }
      },
      // 用户额度分类管理路由（管理员）
      {
        path: 'user-balance-management',
        name: 'UserBalanceManagement',
        component: () => import('@/views/admin/UserBalanceManagement.vue'),
        alias: ['/admin/user-balance-management'],
        meta: { title: '用户额度管理', requiresAuth: true, adminOnly: true }
      },
      // 用户偏好管理路由（管理员）
      {
        path: 'user-preference-management',
        name: 'UserPreferenceManagement',
        component: () => import('@/views/admin/UserPreferenceManagement.vue'),
        alias: ['/admin/user-preference-management'],
        meta: { title: '用户偏好管理', requiresAuth: true, adminOnly: true }
      },
      // 余额修复记录路由（管理员）
      {
        path: 'balance-fix-records',
        name: 'BalanceFixRecords',
        component: () => import('@/views/admin/BalanceFixRecords.vue'),
        alias: ['/admin/balance-fix-records'],
        meta: { title: '余额修复记录', requiresAuth: true, adminOnly: true }
      },
      // 余额异常监控路由（管理员，财务中心子菜单）
      {
        path: 'balance-anomalies',
        name: 'BalanceAnomalies',
        component: () => import('@/views/finance/BalanceAnomalies.vue'),
        meta: { title: '余额异常', requiresAuth: true, adminOnly: true }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// ⚠️ 移除模块级的静态变量 isValid，改为在需要时动态调用 isAuthValid()
// const isValid = isAuthValid(); // 不再使用静态变量

// 记录当前授权源和状态
const logAuthStatus = () => {
  // 获取认证来源和时间
  const loginSource = localStorage.getItem('login_source') || '未知';
  const loginTime = localStorage.getItem('login_time') || '未知';
  const tokenSetTime = localStorage.getItem('token_set_time') || '未知';
  
  // 获取认证信息
  const token = getToken();
  const tokenExist = !!token;
  // ✅ 修复：动态调用 isAuthValid() 获取最新认证状态
  const authValid = isAuthValid();
  
  console.log('路由守卫 - 授权状态:', {
    tokenExist, 
    isValid: authValid,  // 使用动态计算的值
    source: loginSource,
    loginTime,
    tokenSetTime,
    tokenLength: token ? token.length : 0
  });
  
  return { tokenExist, isValid: authValid, source: loginSource };
}

// 路由守卫
router.beforeEach(async (to, from, next) => {
  console.log(`🛡️ 路由守卫: 从 ${from.path} 到 ${to.path}`)
  console.log(`🔍 目标路由完整信息:`, {
    path: to.path,
    fullPath: to.fullPath,
    query: to.query,
    params: to.params,
    meta: to.meta,
    name: to.name
  })
  
  // ⭐ 最优先：直接放行注册申请页面（公开访问，无需任何验证）（已注释，使用外部飞书表单）
  // if (to.path === '/registration/apply') {
  //   console.log('🎯 直接放行注册申请页面（公开访问）');
  //   next();
  //   return;
  // }
  
  // 🔧 定义公共页面（无需认证），包括登录、注册、重置密码等
  const publicPages = ['/login', '/register', '/reset-password', '/forgot-password', '/activate'];
  
  // 特别关注注册页面的访问
  if (to.path === '/register') {
    console.log('📝 访问注册页面，详细信息:')
    console.log('   - 查询参数:', to.query)
    console.log('   - 邀请码参数:', to.query.invite)
    console.log('   - 完整路径:', to.fullPath)
    console.log('   - 来源页面:', from.path)
  }
  
  // 🔧 公共页面白名单：跳过所有认证检查，直接允许访问
  if (publicPages.includes(to.path)) {
    console.log(`✅ 访问公共页面 ${to.path}，跳过认证检查，直接允许访问`);
    next();
    return;
  }
  
  // 检查token是否存在
  const tokenExist = !!getToken();
  console.log('Token存在:', tokenExist);
  
  // 检查URL中是否包含token参数（飞书登录回调等场景）
  const hasTokenInUrl = to.query.access_token || to.hash.includes('access_token');
  
  if (to.meta.requiresAuth) {
    // 检查token有效性
    const authValid = isAuthValid();
    
    if (!tokenExist || !authValid) {
      console.log('未找到token，重定向到登录页');
      if (isEmailChangeCurrentConfirmationRoute(to.path, to.query)) {
        rememberPendingEmailChangeRedirect(to.fullPath)
      }
      next({ name: 'Login', query: { redirect: to.fullPath } });
      return;
    }
    
    // 检查是否需要管理员权限
    if (to.meta.adminOnly) {
      // 从localStorage获取用户信息
      let userInfo;
      try {
        // 尝试多个可能的key
        const userInfoStr = localStorage.getItem('user_info') || localStorage.getItem('user');
        if (!userInfoStr) {
          console.error('未找到用户信息');
          
          // ⚠️ 不再在路由守卫中尝试重新获取用户信息，避免无限循环
          // 直接跳转到登录页
          ElMessage.error(routerT('auth.router.userInfoExpired'));
          next({ name: 'Login', query: { redirect: to.fullPath } });
          return;
        }
        
        userInfo = JSON.parse(userInfoStr);
        
        // 添加安全的空值检查
        if (!userInfo || typeof userInfo !== 'object') {
          console.error('用户信息格式错误');
          ElMessage.error(routerT('auth.router.userInfoAbnormal'));
          next({ name: 'Login', query: { redirect: to.fullPath } });
          return;
        }
        
        console.log('检查管理员权限，用户信息:', userInfo);
        
        // 仅平台管理员角色可过纯 adminOnly；禁止仅凭 is_admin 放行
        // （避免 OPERATOR 误标 is_admin 后进入财务/权限等页）
        const isAdmin = userInfo.username === 'admin' || 
                       userInfo.role === 'ADMIN' || 
                       userInfo.role === 'SUPER_ADMIN' ||
                       (userInfo.is_admin === true &&
                        (userInfo.role === 'ADMIN' || userInfo.role === 'SUPER_ADMIN'));

        // 运营/产品：若路由声明了 requiresPermission，交给后续菜单权限校验
        // （避免侧栏可见却被 adminOnly 挡回）
        const hasStaffPermissionMeta = Boolean(to.meta.requiresPermission)
                       
        if (!isAdmin && !hasStaffPermissionMeta) {
          console.log('非管理员用户尝试访问管理员页面', { role: userInfo.role, is_admin: userInfo.is_admin });
          ElMessage.error(routerT('auth.router.noAdminPermission'));
          next({ path: '/target-analysis-v2' }); // 重定向到 V2 报告生成页
          return;
        }
        
        if (isAdmin) {
          console.log('管理员权限验证通过', { role: userInfo.role, is_admin: userInfo.is_admin });
        } else {
          console.log('非管理员，改由菜单权限校验', {
            role: userInfo.role,
            requiresPermission: to.meta.requiresPermission
          });
        }
      } catch (error) {
        console.error('解析用户信息失败:', error);
        ElMessage.error(routerT('auth.router.getUserInfoFailed'));
        next({ name: 'Login', query: { redirect: to.fullPath } });
        return;
      }
    }

    const requiredPermissions = Array.isArray(to.meta.requiresPermission)
      ? to.meta.requiresPermission
      : (to.meta.requiresPermission ? [to.meta.requiresPermission] : [])
    if (requiredPermissions.length > 0) {
      try {
        const { hasPermission } = useMenuPermission()
        for (const permission of requiredPermissions) {
          const canAccess = await hasPermission(permission)
          if (!canAccess) {
            console.log('用户缺少路由功能权限', { permission, path: to.path })
            ElMessage.error(routerT('permission.noAccessKnowledgeBase'))
            next({ path: '/target-analysis-v2' })
            return
          }
        }
      } catch (error) {
        console.error('检查路由功能权限失败:', error)
        ElMessage.error(routerT('permission.noAccessKnowledgeBase'))
        next({ path: '/target-analysis-v2' })
        return
      }
    }
    
    console.log('已找到有效token，允许访问');
    next();
  } else if (to.path === '/login' && from.path !== '/reset-password' && tokenExist && isAuthValid() && !hasTokenInUrl) {
    // ✅ 修复：改为动态调用 isAuthValid()，确保每次都获取最新的认证状态
    console.log('已登录，重定向到首页');
    next({ path: '/' });
  } else {
    console.log('不需要认证，允许访问');
    next();
  }
})

// 设置路由错误处理
setupRouterErrorHandling(router)

// ==================== 页面访问埋点 ====================
// 用于记录页面进入时间
let pageEnterTime = Date.now()

// 路由后置守卫：页面访问埋点
router.afterEach((to, from) => {
  try {
    // 记录上一个页面的停留时长
    if (from.path && from.path !== '/' && from.name) {
      const duration = Date.now() - pageEnterTime
      console.log(`[Analytics] 页面离开: ${from.path}, 停留时长: ${duration}ms`)
      
      // 上报页面离开事件（带停留时长）
      analytics.track('page_leave', 'page_view', {
        page_url: from.path,
        page_title: from.meta?.title || from.name,
        duration: duration
      })
    }
    
    // 记录新页面进入时间
    pageEnterTime = Date.now()
    
    // 上报页面访问事件
    const pageUrl = to.path
    const pageTitle = to.meta?.titleKey
      ? routerT(to.meta.titleKey)
      : (to.meta?.title || to.name || routerT('router.unnamed'))
    document.title = pageTitle
    
    console.log(`[Analytics] 页面访问: ${pageUrl} (${pageTitle})`)
    
    // 判断入口来源
    let entryPoint = 'direct'
    if (from.path && from.path !== '/') {
      entryPoint = 'internal' // 内部跳转
    } else if (document.referrer) {
      entryPoint = 'referrer' // 外部来源
    }
    
    // 上报页面访问事件
    analytics.pageView(pageUrl, {
      page_title: pageTitle,
      entry_point: entryPoint,
      referrer_url: from.path || document.referrer || null,
      route_name: to.name,
      route_params: to.params,
      route_query: to.query
    })
    
  } catch (error) {
    console.error('[Analytics] 页面埋点上报失败:', error)
  }
})

// 页面卸载时上报最后一个页面的停留时长
window.addEventListener('beforeunload', () => {
  try {
    const currentRoute = router.currentRoute.value
    if (currentRoute.path && currentRoute.path !== '/') {
      const duration = Date.now() - pageEnterTime
      console.log(`[Analytics] 页面卸载: ${currentRoute.path}, 停留时长: ${duration}ms`)
      
      // 使用同步方式上报（因为页面即将关闭）
      analytics.track('page_unload', 'page_view', {
        page_url: currentRoute.path,
        page_title: currentRoute.meta?.title || currentRoute.name,
        duration: duration
      })
      
      // 立即刷新队列
      analytics.flush()
    }
  } catch (error) {
    console.error('[Analytics] 页面卸载埋点上报失败:', error)
  }
})

console.log('[Analytics] 页面访问埋点已启用')

export default router
